/**
 * TravelSummaryView — one-page aggregate view of a trip.
 *
 * Composes the existing feature panels into collapsible sections so the
 * traveler can see budget, map, dossier, and the day-by-day itinerary
 * without jumping between tabs. Each section respects its feature flag:
 * if a feature is disabled in settings the section is hidden entirely.
 *
 * This replaces the previous `Itinerary` tab. The day-by-day content is
 * still rendered by TravelItinerarySection (shared with TravelListView).
 */

import { DollarSign, FileText, ListOrdered, Map as MapIcon } from 'lucide-react';
import type {
  TravelAttachmentResponse,
  TravelEventResponse,
  TravelItemResponse,
  TravelTripResponse,
} from '../api';
import { useTravelFeature } from '../hooks';
import { TravelBudgetPanelBody } from './TravelBudgetPanel';
import { TravelDossierInline } from './TravelDossierInline';
import { TravelItinerarySection } from './TravelItinerarySection';
import { TravelMapView } from './TravelMapView';
import { TravelCollapsibleSection } from './TravelSummaryCollapse';

interface TravelSummaryViewProps {
  trip: TravelTripResponse;
  items: TravelItemResponse[];
  events: TravelEventResponse[];
  attachments?: TravelAttachmentResponse[];
  activeItemId: string | null;
  onSelectItem: (itemId: string) => void;
}

export function TravelSummaryView({
  trip,
  items,
  events,
  attachments = [],
  activeItemId,
  onSelectItem,
}: TravelSummaryViewProps) {
  const mapFeature = useTravelFeature('map');
  const dossierFeature = useTravelFeature('dossier');
  const budgetFeature = useTravelFeature('budget');

  const dayCount = items.filter((i) => i.itemType === 'day').length;
  const totalEvents = events.length;
  const allCities = new Set<string>();
  const allCountries = new Set<string>();
  for (const i of items) {
    if (i.city) allCities.add(i.city);
    if (i.country) allCountries.add(i.country);
    if (i.locations) {
      for (const loc of i.locations) {
        if (loc.city) allCities.add(loc.city);
        if (loc.country) allCountries.add(loc.country);
      }
    }
  }

  const missingCoordCount = items.filter(
    (i) => i.itemType === 'day' && (i.lat == null || i.lng == null),
  ).length;

  return (
    <div className="max-w-3xl mx-auto space-y-5">
      {/* Trip header */}
      <div className="pb-4 border-b border-border">
        <h1 className="text-3xl font-bold tracking-tight">{trip.title}</h1>
        <div className="flex flex-wrap gap-x-6 gap-y-1 mt-2 text-sm text-muted-foreground">
          <span>{dayCount} days</span>
          <span>{totalEvents} events</span>
          {allCities.size > 0 && <span>{allCities.size} cities</span>}
          {allCountries.size > 0 && <span>{allCountries.size} countries</span>}
        </div>
      </div>

      {budgetFeature.enabled && (
        <TravelCollapsibleSection
          title="Budget"
          icon={<DollarSign className="h-4 w-4" />}
          defaultOpen={false}
        >
          <TravelBudgetPanelBody tripId={trip.tripId} />
        </TravelCollapsibleSection>
      )}

      {mapFeature.enabled && (
        <TravelCollapsibleSection
          title="Map"
          icon={<MapIcon className="h-4 w-4" />}
          meta={
            missingCoordCount > 0
              ? `${missingCoordCount} day${missingCoordCount === 1 ? '' : 's'} missing pins`
              : undefined
          }
          defaultOpen={false}
        >
          <div className="h-[420px] rounded-md overflow-hidden border border-border">
            <TravelMapView
              trip={trip}
              items={items}
              events={events}
              activeItemId={activeItemId}
              onSelectItem={onSelectItem}
            />
          </div>
        </TravelCollapsibleSection>
      )}

      {dossierFeature.enabled && (
        <TravelCollapsibleSection
          title="Destination dossier"
          icon={<FileText className="h-4 w-4" />}
          defaultOpen={false}
        >
          <TravelDossierInline tripId={trip.tripId} />
        </TravelCollapsibleSection>
      )}

      <TravelCollapsibleSection
        title="Itinerary"
        icon={<ListOrdered className="h-4 w-4" />}
        meta={`${dayCount} day${dayCount === 1 ? '' : 's'} · ${totalEvents} event${totalEvents === 1 ? '' : 's'}`}
        defaultOpen
      >
        <TravelItinerarySection
          trip={trip}
          items={items}
          events={events}
          attachments={attachments}
        />
      </TravelCollapsibleSection>
    </div>
  );
}
